import Button from "components/form/Button";
import { CanceledAgreementIcon } from "icons/action/CancelAgreement";
import { ExpiredLink } from "icons/action/ExpiredLink";
import OutlineInfoIcon from "icons/payments/OutlineInfoIcon";
import moment from "moment";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { sendAgreementRenew } from "store/slices/paymentsSlice";
import { RootState } from "store/store";
import { decode, encode, formatNumber } from "utils/HelperFunctions";
import { useTestAttrsFactory } from "utils/testid";

const PaymentAgreementExpired = ({
  setIsAgreementSent,
  setIsAgreementSuccess,
  setAgreementFailedReason,
  Cancelled = false,
  startDate,
  agreement_id,
}: any) => {
  const dispatch = useAppDispatch();
  const { id: actionId } = useParams();
  const clusterId = decode(actionId || "")?.split("_")[1];
  let cluster_id = encode(`${clusterId}`, "ci_");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const testAttrs = useTestAttrsFactory();
  const { companyDetails } = useAppSelector(
    (state: RootState) => state.payment
  );

  const sendRenewEmail = async () => {
    setIsLoading(true);
    try {
      if (!agreement_id || !clusterId) {
        console.warn("sendRenewEmail skipped: missing required values", {
          agreement_id,
          cluster_id: clusterId,
        });
        setIsLoading(false);
        return;
      }

      let payload = {
        agreement_id: agreement_id,
        cluster_id: clusterId,
      };
      let res: any = await dispatch(sendAgreementRenew(payload)).unwrap();
      if (res.status === 200 || res.status === 201) {
        const { data, status } = res?.body;
        setIsAgreementSent(true);
        if (status) {
          setIsAgreementSuccess(true);
        } else {
          setAgreementFailedReason(data?.msg || "Agreement Sent Failed");
          setIsAgreementSuccess(false);
        }
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log("sendRenewEmail error =====", error);
    }
  };

  const navigate = useNavigate();
  return (
    <div
      className="h-full w-full grid place-items-center"
      {...testAttrs(
        Cancelled ? "agreement-cancelled" : "agreement-expired",
        Cancelled ? "Payment agreement cancelled" : "Payment agreement expired"
      )}
    >
      <div className="lg:py-0 p-5 flex items-center justify-center lg:h-full flex-col text-center w-full max-w-[630px]">
        {Cancelled ? (
          <CanceledAgreementIcon aria-hidden="true" />
        ) : (
          <ExpiredLink aria-hidden="true" />
        )}
        <div className="lg:text-2xl text-xl mt-10 w-full break-words">
          {Cancelled
            ? "Payment agreement cancelled"
            : "Payment agreement expired"}
        </div>
        <div className="text-sm lg:text-base mt-4 text-txtParagraph">
          {Cancelled
            ? `We're sorry, but the agreement you are trying to access has been cancelled.`
            : `Oops! The agreement you are attempting to access is no longer active.`}
        </div>
        <div
          className={`text-sm lg:text-base mt-4 text-txtParagraph font-normal ${
            Cancelled ? "" : ""
          }`}
        >
          {Cancelled ? (
            <>
              If you need further assistance, please contact{" "}
              <span className="font-bold">
                {companyDetails?.name ||
                  companyDetails?.company_name ||
                  "Palm Labs LLC"}
              </span>{" "}
              at{" "}
              <span className="font-bold">
                {companyDetails?.email || "support@dcdial.com"}
              </span>{" "}
              or call at{" "}
              <span className="font-bold">
                {formatNumber(
                  companyDetails.phone_number,
                  companyDetails.countryCode || "1",
                  true
                ) || "+1 (404) 410 4100"}
              </span>
              .
            </>
          ) : moment(startDate).isAfter(moment(), "day") ? (
            `If you're interested in a new agreement, please click the button below
        to submit your request. We'll review it and get back to you as soon as
        possible.`
          ) : (
            `If you're interested in a new agreement, please reach out to our support team at ${
              companyDetails?.email || "support@dcdial.com"
            } or give us a call at ${
              formatNumber(
                companyDetails.phone_number,
                companyDetails.countryCode || "1",
                true
              ) || "+1 (404) 410 4100"
            }.`
          )}
        </div>
        {Cancelled ? (
          <></>
        ) : (
          <>
            {moment(startDate).isAfter(moment(), "day") && (
              <div className="text-[13px] inline-flex gap-[5px] mt-10 text-txtParagraph justify-center text-center px-14">
                <span className="w-[14px] h-[14px] mt-[2px]">
                  <OutlineInfoIcon aria-hidden="true" />
                </span>
                <span className="w-full max-w-[380px]">
                  Please note, submitting a request does not guarantee approval,
                  but we're here to assist you through the process.
                </span>
              </div>
            )}
            {moment(startDate).isAfter(moment(), "day") ? (
              <div className="mt-10">
                <Button
                  text="Request new agreement"
                  className="!w-[250px]"
                  {...testAttrs(
                    "request-new-agreement",
                    "Request new agreement"
                  )}
                  onClick={() => sendRenewEmail()}
                  isLoading={isLoading}
                />
              </div>
            ) : null}
          </>
        )}
        <div
          className="mt-[24px] lg:text-base text-sm cursor-pointer text-blue hover:!text-btnPrimaryHover"
          role="button"
          tabIndex={0}
          {...testAttrs("back-to-dashboard", "Back to dashboard")}
          onClick={() => navigate(`/${cluster_id}/dashboard`)}
        >
          Back to dashboard
        </div>
      </div>
    </div>
  );
};

export default PaymentAgreementExpired;
